import { useState, useEffect } from 'react';
import { apiFetch } from '../../api';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from '@/components/ui/badge';
import { Loader2, Trophy, Clock, Activity, BarChart3 } from 'lucide-react';

interface FlowLeaderboardModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    executionId: string | null;
    nodeId: string | null;
    onSelectModel?: (model: any) => void;
}

export default function FlowLeaderboardModal({ open, onOpenChange, executionId, nodeId, onSelectModel }: FlowLeaderboardModalProps) {
    const [candidates, setCandidates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !executionId || !nodeId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const filename = `${nodeId}_candidates.json`;
                // apiFetch handles base URL and token automatically
                const data = await apiFetch(`/flows/artifacts/${executionId}/${filename}`);

                if (data) {
                    setCandidates(Array.isArray(data) ? data : []);
                    // console.log("Leaderboard Data:", data);
                    const best = Array.isArray(data) ? data.find((c: any) => c.IsBest) : null;
                    if (best) console.log("Best Model Features:", best?.FeatureImportance);
                } else {
                    setError("No leaderboard data found for this run.");
                }
            } catch (e: any) {
                console.error(e);
                // apiFetch throws errors with messages
                setError("Failed to load leaderboard." + (e.message ? ` (${e.message})` : ""));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, executionId, nodeId]);

    // Helper to clean up "ReplaceMissingValues=>OneHot...=>FastTree"
    const formatTrainerName = (raw: string) => {
        if (!raw) return "Unknown Model";
        // Common ML.NET Algorithms
        const algos = {
            'FastTree': 'Fast Forest (Gradient Boosting)',
            'FastForest': 'Fast Forest (Random Forest)',
            'LightGbm': 'LightGBM (State-of-the-Art)',
            'LbfgsLogisticRegression': 'Logistic Regression',
            'Sdca': 'SDCA (Stochastic Dual Coordinate)',
            'SdcaMaximumEntropy': 'Max Entropy',
            'LbfgsPoissonRegression': 'Poisson Regression',
            'Gam': 'GAM (Generalized Additive)',
            'AveragedPerceptron': 'Perceptron',
            'LinearSvm': 'Linear SVM'
        };

        // Find matching key
        for (const [key, label] of Object.entries(algos)) {
            if (raw.includes(key)) return label;
        }

        // Fallback: Try to take the last meaningful part
        const parts = raw.split('=>').filter(p => p !== 'Unknown' && p !== 'ReplaceMissingValues' && p !== 'OneHotEncoding' && p !== 'Concatenate');
        return parts.length > 0 ? parts[parts.length - 1] : raw;
    };

    const handleSelectModel = async (candidate: any) => {
        if (!executionId || !nodeId) return;
        try {
            setLoading(true);

            // apiFetch handles base URL and token
            const res = await apiFetch('/flows/promote-model', null, {
                method: 'POST',
                body: JSON.stringify({
                    executionId,
                    nodeId,
                    selectedPath: candidate.Path
                })
            });

            // apiFetch returns the parsed JSON body on success, or throws on error.
            if (res) {
                // Update local state to reflect change
                setCandidates(prev => prev.map(c => ({
                    ...c,
                    IsBest: c.Id === candidate.Id
                })));
                if (onSelectModel) onSelectModel(candidate);
            }
        } catch (e: any) {
            console.error(e);
            setError("Error selecting model: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* Increased Size: max-w-6xl */}
            <DialogContent className="max-w-6xl h-[85vh] flex flex-col p-0 overflow-hidden bg-white/95 backdrop-blur-xl border-gray-100/50 shadow-2xl">

                {/* SageMaker-Style Header */}
                <div className="border-b border-slate-200 bg-slate-50/50 p-6 shrink-0 flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <Trophy className="h-5 w-5 text-indigo-600" />
                            Model Leaderboard
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Compare metrics and select the best model for deployment.
                        </p>
                    </div>
                </div>

                {/* Confusion Matrix (For Classification Best Model) */}
                {candidates.some(c => c.IsBest && c.ConfusionMatrix) && (
                    <div className="p-6 border-t border-slate-100 bg-white">
                        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <Activity className="h-4 w-4 text-orange-500" />
                            Confusion Matrix
                        </h3>

                        <div className="overflow-x-auto">
                            <table className="min-w-full text-xs border border-slate-200">
                                <thead>
                                    <tr>
                                        <th className="bg-slate-50 p-2 border-r border-b border-slate-200">Actual \ Predicted</th>
                                        {candidates.find(c => c.IsBest)?.ConfusionMatrix[0].map((_: any, idx: number) => (
                                            <th key={idx} className="bg-slate-50 p-2 border-b border-slate-200 font-semibold text-slate-600">Class {idx}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {candidates.find(c => c.IsBest)?.ConfusionMatrix.map((row: any[], rIdx: number) => (
                                        <tr key={rIdx}>
                                            <td className="bg-slate-50 p-2 border-r border-slate-200 font-semibold text-slate-600">Class {rIdx}</td>
                                            {row.map((val: number, cIdx: number) => (
                                                <td key={cIdx} className={`p-2 text-center border-b border-slate-100 ${rIdx === cIdx ? 'bg-green-50 text-green-700 font-bold' : (val > 0 ? 'bg-red-50 text-red-600' : 'text-slate-300')}`}>
                                                    {val}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Feature Importance Analysis (For Best Model) */}
                {candidates.some(c => c.IsBest && c.FeatureImportance) && (
                    <div className="p-6 border-t border-slate-100 bg-slate-50/30">
                        <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-indigo-500" />
                            Key Drivers (Feature Importance)
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {candidates.find(c => c.IsBest)?.FeatureImportance.map((f: any, idx: number) => (
                                <div key={idx} className="group">
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="font-medium text-slate-700 truncate" title={f.Feature}>{f.Feature}</span>
                                        <span className="font-mono text-slate-500">{(f.Importance * 100).toFixed(2)}%</span>
                                    </div>
                                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-indigo-500 rounded-full group-hover:bg-indigo-600 transition-colors"
                                            style={{
                                                width: `${Math.min(100, Math.max(0, (Math.abs(f.Importance) / Math.max(...candidates.find(c => c.IsBest).FeatureImportance.map((x: any) => Math.abs(x.Importance)))) * 100))}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-4 text-right">
                            * Metric Impact (Permutation Importance)
                        </p>
                    </div>
                )}

                <div className="flex-1 overflow-auto bg-white p-0">
                    {loading && (
                        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-4">
                            <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
                            <p>Analyzing Model Candidates...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="m-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-100 flex items-center gap-3">
                            <Activity className="h-5 w-5" /> {error}
                        </div>
                    )}

                    {!loading && !error && candidates.length > 0 && (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                                <tr>
                                    <th className="p-4 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16 text-center">Rank</th>
                                    <th className="p-4 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">Model Name</th>
                                    <th className="p-4 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider w-1/3">
                                        {candidates[0].RSquared !== undefined || candidates[0].R2 !== undefined ? 'R² Score' : 'Accuracy'}
                                    </th>
                                    <th className="p-4 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">
                                        {candidates[0].MAE ? 'MAE' : 'Log Loss'}
                                    </th>
                                    <th className="p-4 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">
                                        Training Time
                                    </th>
                                    <th className="p-4 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {candidates.map((c, idx) => {
                                    const score = c.RSquared ?? c.R2 ?? c.Accuracy ?? c.MicroAccuracy ?? 0;
                                    const scorePct = Math.max(0, Math.min(100, score * 100)); // Clamp 0-100

                                    return (
                                        <tr key={idx} className={`group hover:bg-slate-50/50 transition-colors ${c.IsBest ? 'bg-indigo-50/30' : ''}`}>
                                            {/* Rank */}
                                            <td className="p-4 text-center">
                                                <div className={`
                                                    inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold
                                                    ${c.IsBest ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 bg-slate-100'}
                                                `}>
                                                    #{c.Rank}
                                                </div>
                                            </td>

                                            {/* Model Name */}
                                            <td className="p-4">
                                                <div className="font-medium text-slate-800">
                                                    {formatTrainerName(c.Trainer)}
                                                </div>
                                                <div className="text-xs text-slate-400 font-mono mt-0.5 max-w-[200px] truncate" title={c.Trainer}>
                                                    {c.Trainer}
                                                </div>
                                            </td>

                                            {/* Primary Metric (Bar) */}
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${c.IsBest ? 'bg-indigo-500' : 'bg-slate-300'}`}
                                                            style={{ width: `${scorePct}%` }}
                                                        />
                                                    </div>
                                                    <span className={`font-mono font-medium ${c.IsBest ? 'text-indigo-700' : 'text-slate-600'}`}>
                                                        {score.toFixed(4)}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Secondary Metric */}
                                            <td className="p-4">
                                                <span className="text-slate-600 font-mono text-sm">
                                                    {c.MAE ? c.MAE.toFixed(4) : (c.LogLoss ? c.LogLoss.toFixed(4) : '-')}
                                                </span>
                                            </td>

                                            {/* Duration */}
                                            <td className="p-4">
                                                <span className="text-slate-500 text-sm">
                                                    {c.DurationSec?.toFixed(1)}s
                                                </span>
                                            </td>

                                            {/* Action */}
                                            <td className="p-4 text-right">
                                                {c.IsBest ? (
                                                    <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-100 cursor-default">
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                                                        onClick={() => handleSelectModel(c)}
                                                    >
                                                        Select
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {false && (
                    <div className="flex-1 overflow-y-auto bg-slate-50/50 p-6">
                        {loading && (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-4">
                                <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
                                <p>Analyzing Model Candidates...</p>
                            </div>
                        )}

                        {!loading && error && (
                            <div className="p-6 bg-red-50 text-red-600 rounded-xl border border-red-100 flex items-center gap-3">
                                <div className="p-2 bg-red-100 rounded-full"><Activity className="h-5 w-5" /></div>
                                {error}
                            </div>
                        )}

                        {!loading && !error && candidates.length > 0 && (candidates[0].Accuracy === 1 || candidates[0].R2 === 1) && (
                            <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200 flex items-start gap-4">
                                <div className="p-2 bg-amber-100 rounded-full shrink-0">
                                    <Activity className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-amber-800 text-sm">Suspiciously Perfect Accuracy (100%)</h4>
                                    <p className="text-amber-700 text-xs mt-1 leading-relaxed">
                                        Achieving 100% accuracy is rare and often indicates <strong>Data Leakage</strong>.
                                        This happens when the training data includes columns that directly reveal the answer (e.g. 'DenialReason' predicting 'Status').
                                        <br /><br />
                                        <strong>Recommendation:</strong> Remove highly correlated columns from your input data and retrain.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!loading && !error && candidates.length > 0 && (
                            <div className="space-y-4">
                                {candidates.map((c, idx) => (
                                    <div
                                        key={idx}
                                        className={`
                                        group relative overflow-hidden rounded-xl border transition-all duration-300 hover:shadow-lg
                                        ${c.IsBest
                                                ? 'bg-white border-yellow-400/50 shadow-md ring-1 ring-yellow-400/20'
                                                : 'bg-white border-gray-200 hover:border-indigo-300'}
                                    `}
                                    >
                                        {/* Rank Indicator Strip */}
                                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${c.IsBest ? 'bg-gradient-to-b from-yellow-400 to-orange-500' : 'bg-gray-200 group-hover:bg-indigo-400'}`} />

                                        <div className="flex items-center p-5 pl-8 gap-6">

                                            {/* Rank */}
                                            <div className="flex flex-col items-center justify-center w-12 shrink-0 relative group/rank">
                                                <span className={`text-xs font-bold uppercase tracking-wider ${c.IsBest ? 'text-yellow-600' : 'text-gray-400'}`}>Rank</span>
                                                <span className={`text-3xl font-black ${c.IsBest ? 'text-yellow-500' : 'text-gray-300'}`}>#{c.Rank}</span>

                                                {/* Rank Explanation Tooltip */}
                                                <div className="absolute left-14 top-1/2 -translate-y-1/2 w-64 bg-slate-800 text-white text-xs p-3 rounded-lg shadow-xl opacity-0 group-hover/rank:opacity-100 transition-opacity pointer-events-none z-50">
                                                    <p className="font-bold mb-1">Why Rank #{c.Rank}?</p>
                                                    {c.IsBest ? (
                                                        <p>This model achieved the highest <strong>{c.RSquared !== undefined ? 'R² Score' : 'Accuracy'}</strong> ({c.RSquared !== undefined ? c.RSquared.toFixed(4) : ((c.Accuracy ?? c.MicroAccuracy ?? 0) * 100).toFixed(2)}%) compared to all other candidates.</p>
                                                    ) : (
                                                        <p>This model performed worse than the winner. Its {c.RSquared !== undefined ? 'R² Score' : 'Accuracy'} was {((candidates[0].Accuracy || candidates[0].MicroAccuracy || candidates[0].R2) - (c.Accuracy || c.MicroAccuracy || c.R2)).toFixed(4)} lower than the best model.</p>
                                                    )}
                                                    <div className="mt-2 text-[10px] text-slate-400">algorithm: {formatTrainerName(c.Trainer)}</div>
                                                </div>
                                            </div>

                                            {/* Model Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-lg font-bold text-gray-900 truncate">
                                                        {formatTrainerName(c.Trainer)}
                                                    </h3>
                                                    {c.IsBest && (
                                                        <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200 px-2 py-0.5 text-xs">
                                                            Best Model
                                                        </Badge>
                                                    )}
                                                </div>
                                                {/* Cleaned up description instead of raw pipeline */}
                                                <p className="text-xs text-gray-400 font-medium">
                                                    {c.Trainer.includes('FastTree') ? 'Gradient Boosting Decision Tree' :
                                                        c.Trainer.includes('LightGbm') ? 'Light Gradient Boosting Machine' :
                                                            c.Trainer.includes('FastForest') ? 'Random Forest Ensemble' :
                                                                c.Trainer.includes('LbfgsLogisticRegression') ? 'Logistic Regression Classifier' :
                                                                    c.Trainer.includes('Sdca') ? 'Stochastic Dual Coordinate Ascent' : 'Automated Model Pipeline'}
                                                </p>
                                            </div>

                                            {/* Metrics Grid */}
                                            <div className="flex items-center gap-8 shrink-0">
                                                {/* Primary Metric */}
                                                <div className="flex flex-col items-end">
                                                    <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                                                        <BarChart3 className="h-3 w-3" />
                                                        {c.RSquared !== undefined ? 'R² Score' : 'Accuracy'}
                                                    </span>
                                                    <span className="text-xl font-bold text-slate-800">
                                                        {c.RSquared !== undefined ? c.RSquared.toFixed(4) : ((c.Accuracy ?? c.MicroAccuracy ?? 0) * 100).toFixed(2) + '%'}
                                                    </span>
                                                </div>

                                                {/* Secondary Metric */}
                                                <div className="flex flex-col items-end hidden sm:flex">
                                                    <span className="text-xs font-medium text-gray-500 uppercase">
                                                        {c.MAE ? 'MAE' : 'Log Loss'}
                                                    </span>
                                                    <span className="text-base font-semibold text-slate-600">
                                                        {c.MAE ? c.MAE.toFixed(4) : (c.LogLoss ? c.LogLoss.toFixed(4) : '-')}
                                                    </span>
                                                </div>

                                                {/* Duration */}
                                                <div className="flex flex-col items-end w-20">
                                                    <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                                                        <Clock className="h-3 w-3" /> Time (s)
                                                    </span>
                                                    <span className="text-sm font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md mt-1">
                                                        {c.DurationSec?.toFixed(1)}s
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <div className="shrink-0 pl-4 border-l border-gray-100">
                                                <Button
                                                    disabled={c.IsBest}
                                                    onClick={() => handleSelectModel(c)}
                                                    variant={c.IsBest ? "ghost" : "outline"}
                                                    className={`
                                                    ${c.IsBest ? 'text-green-600 bg-green-50' : 'text-gray-600 hover:text-indigo-600 hover:border-indigo-200'}
                                                `}
                                                >
                                                    {c.IsBest ? 'Active' : 'Select'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="p-4 bg-white border-t border-gray-100 flex justify-between items-center shrink-0">
                    <p className="text-xs text-gray-400">
                        Generated by ML.NET AutoML • Training Time: {candidates.reduce((a, b) => a + (b.DurationSec || 0), 0).toFixed(0)}s
                    </p>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close Leaderboard</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
